import type { Observable } from 'rxjs'
import { finalize, shareReplay } from 'rxjs'

export class SharedRequest<T> {
  private request$: Observable<T> | null = null

  /**
   * Returns the in-flight observable or creates one from the factory.
   * The request is shared between concurrent subscribers, replays its result within the same flight,
   * clears after completion or error, and cancels upstream when every subscriber unsubscribes.
   * `finalize` must run before `shareReplay` so the field clears when the upstream finishes, not per subscriber.
   * @param {() => Observable<T>} factory Creates the upstream request for a new flight.
   * @returns {Observable<T>} The shared observable for the current flight.
   */
  share(factory: () => Observable<T>): Observable<T> {
    if (this.request$) {
      return this.request$
    }

    this.request$ = factory().pipe(
      finalize(() => {
        this.request$ = null
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    return this.request$
  }
}
